import { TextSkeleton } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Alert, Stack, ThemeProvider } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { CancelShiftFooter } from "./components/CancelShiftFooter";
import { CancelShiftHeader } from "./components/CancelShiftHeader";
import { CreditProgressSection } from "./components/CreditProgressSection";
import { ScoreBreakdownList } from "./components/ScoreBreakdownList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import { useCancelShiftPageData } from "./hooks/useCancelShiftPageData";

function CancelShiftPageSkeleton(): JSX.Element {
  return (
    <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
      <TextSkeleton variant="h2" width="60%" />
      <TextSkeleton variant="body2" width="80%" />
      <TextSkeleton variant="h1" width="30%" />
      <TextSkeleton variant="body2" width="100%" />
      <TextSkeleton variant="body2" width="100%" />
    </Stack>
  );
}

export function CancelShiftPage(): JSX.Element {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const currentDate = useCurrentDate(1000);

  const {
    isLoading,
    isError,
    currentScore,
    newScore,
    scoreChange,
    policyLabel,
    cancellationCreditStats,
  } = useCancelShiftPageData({
    shiftId,
    currentDate,
  });

  const handleKeepShift = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePressAndHoldToCancel = useCallback(() => {
    // TODO: Implement press and hold functionality in separate ticket
    // For now, this button is shown but does not trigger cancellation
  }, []);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <CancelShiftHeader />

        {isLoading && <CancelShiftPageSkeleton />}

        {isError && (
          <Alert severity="error" sx={{ margin: 4 }}>
            Something went wrong while loading cancellation details. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && (
          <Stack sx={{ flex: 1 }}>
            <ScoreDisplay score={newScore} />

            <CreditProgressSection
              cancellationCreditStats={cancellationCreditStats?.cancellation}
            />

            <ScoreBreakdownList
              currentScore={currentScore}
              scoreChange={scoreChange}
              policyLabel={policyLabel}
            />
          </Stack>
        )}

        <CancelShiftFooter
          isLoading={isLoading}
          onKeepShift={handleKeepShift}
          onPressAndHoldToCancel={handlePressAndHoldToCancel}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
